import type { CreateSummaryResponse, SummaryResult } from "./types";

export type { OperationState, CreateSummaryResponse, AcceptedSummaryResponse, CompletedSummaryResponse } from "./types";

const API_BASE_URL = import.meta.env.VITE_INTELLIGENCE_LAYER_API_URL;

const buildHeaders = (accessToken: string, locale: string): Record<string, string> => ({
  Authorization: `Bearer ${accessToken}`,
  accept: "application/json",
  "content-type": "application/json",
  "Accept-Language": locale,
  "X-Acto-Locale": locale,
});

export async function requestSummary(noteId: string, token: string, locale: string): Promise<CreateSummaryResponse> {
  if (!API_BASE_URL) {
    throw new Error("Missing VITE_INTELLIGENCE_LAYER_API_URL");
  }

  const response = await fetch(`${API_BASE_URL}/meeting_recap/summarize`, {
    method: "POST",
    headers: buildHeaders(token, locale),
    body: JSON.stringify({ note_id: noteId }),
  });

  if (!response.ok) {
    const message = await response.text().catch(() => "");
    throw new Error(message || `Summary request failed (${response.status})`);
  }

  return (await response.json()) as CreateSummaryResponse;
}

// 425 is the job still running; the rest are gateway conditions that clear on their own.
const TRANSIENT_RESULT_STATUSES = new Set([425, 408, 429, 502, 503, 504]);

/** The operation itself failed, expired, or is not the caller's, so polling it again cannot help. */
export class SummaryOperationError extends Error {}

export const isSummaryResult = (payload: unknown): payload is SummaryResult => {
  const summary = (payload as SummaryResult | undefined)?.summary;
  return summary?.type === "text" && typeof summary.text === "string";
};

/**
 * One poll of a summarize operation's result. Returns undefined while the result is still
 * out of reach, which covers both the job running (425, also returned until the summary is
 * written) and a gateway error worth another attempt. Throws `SummaryOperationError` for
 * everything else, including a 500, which the endpoint uses for an operation missing its
 * context, and a 401, so a token refreshed mid-poll ends the recovery as a failure, the same
 * limit the meeting-prep poll carries.
 */
export async function fetchSummaryResult(
  opId: string,
  token: string,
  locale: string,
  signal: AbortSignal,
): Promise<SummaryResult | undefined> {
  if (!API_BASE_URL) {
    throw new Error("Missing VITE_INTELLIGENCE_LAYER_API_URL");
  }

  const response = await fetch(`${API_BASE_URL}/meeting_recap/summarize/operations/${opId}/result`, {
    headers: buildHeaders(token, locale),
    signal,
  });

  if (TRANSIENT_RESULT_STATUSES.has(response.status)) {
    return undefined;
  }
  if (!response.ok) {
    throw new SummaryOperationError(`Summary operation failed (${response.status})`);
  }

  const payload = (await response.json()) as unknown;
  if (!isSummaryResult(payload)) {
    throw new SummaryOperationError("Summary result payload was not understood");
  }

  return payload;
}
